import React, { useState } from 'react';
import { Modal } from '../../components/Modal';
import { ModalFooter } from '../../components/ModalFooter';
import { FormGroup } from '../../components/FormGroup';
import { useCreateReport } from '../../hooks/useReports';

const DATE_FORMAT_LETTERS = [
  ['G', 'Era designator', 'AD'],
  ['y', 'Year', '1996; 96'],
  ['Y', 'Week year', '2009; 09'],
  ['M', 'Month in year', 'July; Jul; 07'],
  ['w', 'Week in year', '27'],
  ['W', 'Week in month', '2'],
  ['D', 'Day in year', '189'],
  ['d', 'Day in month', '10'],
  ['F', 'Day of week in month', '2'],
  ['E', 'Day name in week', 'Tuesday; Tue'],
  ['u', 'Day number of week (1=Mon…7=Sun)', '1'],
  ['a', 'Am/pm marker', 'PM'],
  ['H', 'Hour in day (0-23)', '0'],
  ['k', 'Hour in day (1-24)', '24'],
  ['K', 'Hour in am/pm (0-11)', '0'],
  ['h', 'Hour in am/pm (1-12)', '12'],
  ['m', 'Minute in hour', '30'],
  ['s', 'Second in minute', '55'],
  ['S', 'Millisecond', '978'],
  ['z', 'Time zone (general)', 'PST; GMT-08:00'],
  ['Z', 'Time zone (RFC 822)', '-0800'],
  ['X', 'Time zone (ISO 8601)', '-08; -0800; -08:00'],
];

function defaultName(type: string): string {
  const label = type === 'pre_processing_report' ? 'Pre Processing' : 'Post Processing';
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${label} Report ${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()} @ ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

interface Props {
  projectId: string;
  projectName: string;
  type: 'pre_processing_report' | 'post_processing_report';
  onClose: () => void;
}

export function ProcessingReportModal({ projectId, projectName, type, onClose }: Props) {
  const [name, setName] = useState(() => defaultName(type));
  const [multiValueCharCode, setMultiValueCharCode] = useState(59);
  const [dateTimeFormat, setDateTimeFormat] = useState("yyyy-MM-dd'T'HH:mm:ss.SSSZ");
  const [showDateTimeHelp, setShowDateTimeHelp] = useState(false);

  const createReport = useCreateReport(projectId);

  const charDisplay = multiValueCharCode >= 0 && multiValueCharCode <= 255
    ? String.fromCharCode(multiValueCharCode)
    : '';

  const nameInvalid = !name.trim() || name.length > 255;
  const multiValueInvalid = isNaN(multiValueCharCode) || multiValueCharCode < 0 || multiValueCharCode > 255;
  const dateTimeInvalid = !dateTimeFormat.trim() || dateTimeFormat.length > 255;

  const handleSubmit = () => {
    createReport.mutate(
      { projectId, type, format: 'zip', name, multiValueCharCode, dateTimeFormat, hasHeaderRow: true, fields: [], collectionIds: [], columnFilters: { operator: 0, fields: [], searchExpressions: [] } },
      { onSuccess: onClose },
    );
  };

  const title = type === 'pre_processing_report' ? 'Pre-Processing Report' : 'Post-Processing Report';

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (nameInvalid || multiValueInvalid || dateTimeInvalid) return;
    handleSubmit();
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      onSubmit={handleFormSubmit}
      formClassName="form-horizontal"
      footer={
        <ModalFooter
          onClose={onClose}
          nextLabel="Generate Report"
          nextDisabled={nameInvalid || multiValueInvalid || dateTimeInvalid}
          saving={createReport.isPending}
          savingLabel="Generating…"
        />
      }
    >
      <FormGroup label="Project" htmlFor="project-name" labelCols="col-sm-4" inputCols="col-sm-8">
        <p className="form-control-static"><i className="fas fa-gavel" /> {projectName}</p>
      </FormGroup>

      <FormGroup
        label="Report name"
        htmlFor="report-name"
        labelCols="col-sm-4"
        inputCols="col-sm-8"
        error={name.length > 255 ? 'Name must be less than 255 characters' : undefined}
      >
        <input
          id="report-name"
          type="text"
          className="form-control"
          placeholder="Report Name"
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={createReport.isPending}
          maxLength={255}
        />
      </FormGroup>

      <FormGroup
        label="Multi Value"
        htmlFor="multi-value-char-code"
        labelCols="col-sm-4"
        inputCols="col-sm-8"
        error={multiValueInvalid ? 'Multi Value Char Code is required (0–255)' : undefined}
      >
        <div className="input-group" style={{ maxWidth: 180 }}>
          <input
            id="multi-value-char-code"
            type="number"
            className="form-control"
            value={multiValueCharCode}
            onChange={e => setMultiValueCharCode(parseInt(e.target.value, 10))}
            min={0}
            max={255}
            disabled={createReport.isPending}
          />
          <span className="input-group-addon" style={{ minWidth: 36, textAlign: 'center' }}>
            {charDisplay}
          </span>
        </div>
      </FormGroup>

      <FormGroup
        label={
          <>
            Date Time Format{' '}
            <button
              type="button"
              className="btn btn-default btn-xs"
              onClick={() => setShowDateTimeHelp(v => !v)}
              title="Show definitions"
            >
              <i className="fas fa-question-circle" />
            </button>
          </>
        }
        htmlFor="date-time-format"
        labelCols="col-sm-4"
        inputCols="col-sm-8"
        error={dateTimeFormat.length > 255 ? 'Date Time Format must be less than 255 characters' : undefined}
      >
        <input
          id="date-time-format"
          type="text"
          className="form-control"
          value={dateTimeFormat}
          onChange={e => setDateTimeFormat(e.target.value)}
          disabled={createReport.isPending}
          maxLength={255}
        />
      </FormGroup>

      {showDateTimeHelp && (
        <table className="table table-condensed table-bordered">
          <thead>
            <tr><th>Letter</th><th>Component</th><th>Examples</th></tr>
          </thead>
          <tbody>
            {DATE_FORMAT_LETTERS.map(([letter, component, examples]) => (
              <tr key={letter}>
                <td><code>{letter}</code></td>
                <td>{component}</td>
                <td><code>{examples}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {createReport.isError && (
        <p className="text-danger">Failed to generate report. Please try again.</p>
      )}
    </Modal>
  );
}
